import React from 'react';
import {Link, useLocation} from 'react-router-dom';
import {Logo} from '../components/Icons';
import {resetCodeRequest} from './resetCodeApi';
import './PasswordReset.css';
import {ResetCodeInput} from './ResetCodeInput';

function challengeFromSearch(search) {
  const params=new URLSearchParams(search), id=params.get('requestId'), expires=Number(params.get('expires'));
  return /^[a-f0-9]{64}$/.test(id || '') && Number.isSafeInteger(expires) && expires>0
    ? {id,deadline:Math.min(expires*1000,Date.now()+600000)} : null;
}

export function PasswordReset({match,onReset}) {
  const location=useLocation();
  const [step,setStep]=React.useState('email');
  const [email,setEmail]=React.useState('');
  const [code,setCode]=React.useState('');
  const [password,setPassword]=React.useState('');
  const [confirmation,setConfirmation]=React.useState('');
  const [requestId,setRequestId]=React.useState('');
  const [resetToken,setResetToken]=React.useState('');
  const [deadline,setDeadline]=React.useState(0);
  const [resendAt,setResendAt]=React.useState(0);
  const [clock,setClock]=React.useState(Date.now());
  const [busy,setBusy]=React.useState(false);
  const [error,setError]=React.useState('');
  const lock=React.useRef(false), controller=React.useRef(null), alive=React.useRef(true);
  const flowGeneration=React.useRef(0);
  React.useEffect(()=>{
    const challenge=challengeFromSearch(location.search);
    ++flowGeneration.current; controller.current?.abort(); lock.current=false;
    setBusy(false);setError('');setCode('');setPassword('');setConfirmation('');setResetToken('');setEmail('');setResendAt(0);
    setStep(challenge ? 'code' : 'email');setRequestId(challenge?.id || '');setDeadline(challenge?.deadline || 0);setClock(Date.now());
  },[location.search]);
  React.useEffect(()=>{
    alive.current=true;
    const timer=setInterval(()=>setClock(Date.now()),1000);
    return ()=>{alive.current=false;clearInterval(timer);controller.current?.abort();};
  },[]);
  const remaining=Math.max(0,Math.ceil((deadline-clock)/1000));
  const wait=Math.max(0,Math.ceil((resendAt-clock)/1000));
  const run=async action=>{
    if(lock.current)return;
    lock.current=true;setBusy(true);setError('');
    const generation=flowGeneration.current;
    controller.current=new AbortController();
    try {
      let variables;
      if(action==='request')variables={email:email.trim()};
      if(action==='verify')variables={requestId,code};
      if(action==='complete'){
        if(password!==confirmation){setError('Hesla se neshodují.');return;}
        if(Array.from(password).length<8 || new TextEncoder().encode(password).length>72){setError('Heslo musí mít alespoň 8 znaků a nejvýše 72 bajtů.');return;}
        variables={requestId,resetToken,newPassword:password};
      }
      const result=await resetCodeRequest(action,variables,controller.current.signal);
      if(!alive.current || generation!==flowGeneration.current)return;
      if(result.status==='tryLater'){setResendAt(Date.now()+(result.retryAfter || 60)*1000);setError('Příliš mnoho požadavků. Zkuste to za chvíli znovu.');return;}
      if(action==='request' && result.status==='sent' && /^[a-f0-9]{64}$/.test(result.requestId || '')){
        setRequestId(result.requestId);setCode('');setResetToken('');setStep('code');
        setDeadline(Date.now()+(result.expiresIn || 600)*1000);setResendAt(Date.now()+(result.retryAfter || 60)*1000);setClock(Date.now());
      }else if(action==='verify' && result.status==='verified' && /^[a-f0-9]{64}$/.test(result.resetToken || '')){
        setResetToken(result.resetToken);setCode('');setStep('password');setDeadline(Date.now()+(result.expiresIn || 300)*1000);setClock(Date.now());
      }else if(action==='complete' && result.status==='done'){
        setPassword('');setConfirmation('');setResetToken('');setRequestId('');setStep('done');onReset?.();
      }else if(result.status==='invalidEmail')setError('Zadejte platnou e-mailovou adresu.');
      else if(result.status==='invalidPassword')setError('Heslo musí mít alespoň 8 znaků a nejvýše 72 bajtů.');
      else {
        setError(action==='verify' ? 'Kód není platný, vypršel nebo byl vyčerpán limit 5 pokusů. Vyžádejte si nový.' : 'Platnost resetu skončila. Vyžádejte si nový kód.');
        if(action==='complete'){setResetToken('');setPassword('');setConfirmation('');setStep('code');setDeadline(0);}
      }
    } catch(e){if(alive.current && generation===flowGeneration.current)setError(e.message);}
    finally{if(generation===flowGeneration.current){lock.current=false;if(alive.current)setBusy(false);}}
  };
  const back=()=>{setStep('email');setCode('');setPassword('');setConfirmation('');setRequestId('');setResetToken('');setError('');};
  return <div className="login-wrapper password-reset-code">
    <Logo />
    <section className="reset-card" aria-busy={busy} aria-labelledby="reset-heading">
      <h1 id="reset-heading">{step==='done' ? 'Heslo bylo změněno' : 'Obnovení hesla'}</h1>
      {match?.params?.token && step==='email' && <p>Resetovací odkazy jsme nahradili bezpečnějším kódem. Vyžádejte si nový kód níže.</p>}
      {error && <p className="reset-card__error" role="alert">{error}</p>}
      {step==='email' && <form onSubmit={event=>{event.preventDefault();run('request');}}>
        <p>Na e-mail vám pošleme šestimístný kód pro nastavení nového hesla.</p>
        <label htmlFor="reset-email">E-mail</label>
        <input className="browser-default" id="reset-email" type="email" autoComplete="email" maxLength={150} required value={email} disabled={busy} onChange={event=>setEmail(event.target.value)} />
        <button type="submit" disabled={busy || wait>0}>{busy ? 'Odesílání…' : wait>0 ? `Odeslat za ${wait} s` : 'Poslat resetovací kód'}</button>
      </form>}
      {step==='code' && <form onSubmit={event=>{event.preventDefault();run('verify');}}>
        {email ? <p>Pokud účet <strong>{email.trim()}</strong> existuje a není překročen limit odesílání, přijde vám e-mail s kódem. Zkontrolujte i spam.</p> : <p>Zadejte šestimístný kód z e-mailu od GPSON. Heslo se změní až po jeho ověření a zadání nového hesla.</p>}
        <label id="reset-code-label" htmlFor="reset-code">Šestimístný kód</label>
        <ResetCodeInput value={code} onChange={setCode} disabled={busy}/>
        <p role="status">{remaining ? `Platnost kódu: ${Math.floor(remaining/60)}:${String(remaining%60).padStart(2,'0')}` : 'Platnost kódu skončila. Vyžádejte si nový.'} Nejvýše 5 pokusů.</p>
        <button type="submit" disabled={busy || !/^[0-9]{6}$/.test(code) || remaining===0}>{busy ? 'Ověřování…' : 'Ověřit kód'}</button>
        <button type="button" className="reset-card__secondary" disabled={busy || wait>0} onClick={()=>email ? run('request') : back()}>{wait ? `Nový kód za ${wait} s` : email ? 'Poslat nový kód' : 'Vyžádat nový kód'}</button>
        {email && <button type="button" className="reset-card__secondary" disabled={busy} onClick={back}>Změnit e-mail</button>}
      </form>}
      {step==='password' && <form onSubmit={event=>{event.preventDefault();run('complete');}}>
        <p>Kód je ověřený. Nyní si nastavte nové heslo.</p>
        <label htmlFor="reset-password">Nové heslo</label>
        <input className="browser-default" id="reset-password" type="password" autoComplete="new-password" required minLength={8} value={password} disabled={busy} onChange={event=>setPassword(event.target.value)} autoFocus />
        <label htmlFor="reset-confirmation">Potvrzení nového hesla</label>
        <input className="browser-default" id="reset-confirmation" type="password" autoComplete="new-password" required minLength={8} value={confirmation} disabled={busy} onChange={event=>setConfirmation(event.target.value)} />
        <p>Alespoň 8 znaků. Na dokončení máte 5 minut.</p>
        <button type="submit" disabled={busy || !remaining}>{busy ? 'Ukládání…' : 'Uložit nové heslo'}</button>
        {!remaining && <p role="alert">Platnost ověření skončila. Vyžádejte si nový kód.</p>}
        <button type="button" className="reset-card__secondary" disabled={busy} onClick={back}>Začít znovu</button>
      </form>}
      {step==='done' && <p>Nové heslo je uložené. Přihlaste se znovu do aplikace.</p>}
      <Link to="/login">{step==='done' ? 'Přejít na přihlášení' : 'Zpět na přihlášení'}</Link>
    </section>
  </div>;
}
